import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { RuleBuilder } from '../../lib/components/RuleBuilder';
import { useRulesStore } from '../../stores/rulesStore';
import type { HardRule } from '../../types';

type DialogMode = 'create' | 'edit' | null;

export const HardRules: React.FC = () => {
  const { hardRules, addHardRule, updateHardRule, deleteHardRule, toggleHardRuleActive } = useRulesStore();

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedRule, setSelectedRule] = useState<HardRule | null>(null);

  const handleCreate = () => {
    setSelectedRule(null);
    setDialogMode('create');
  };

  const handleEdit = (rule: HardRule) => {
    setSelectedRule(rule);
    setDialogMode('edit');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this hard rule?')) {
      deleteHardRule(id);
    }
  };

  const handleToggleActive = (id: string) => {
    toggleHardRuleActive(id);
  };

  const handleSubmit = (data: HardRule) => {
    if (dialogMode === 'create') {
      addHardRule(data);
    } else if (dialogMode === 'edit' && selectedRule) {
      updateHardRule(selectedRule.id, data);
    }
    setDialogMode(null);
    setSelectedRule(null);
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedRule(null);
  };

  const getCategoryColor = (category: HardRule['category']) => {
    const colors: Record<HardRule['category'], 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error'> = {
      teacher: 'primary',
      room: 'secondary',
      student: 'success',
      curriculum: 'warning',
      timing: 'info',
      collaboration: 'error',
      scheduling: 'primary',
    };
    return colors[category];
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1">
              Hard Rules
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Non-negotiable constraints that must never be violated during timetable generation
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Add Hard Rule
          </Button>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" color="text.secondary">
              Total Rules
            </Typography>
            <Typography variant="h3">{hardRules.length}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" color="text.secondary">
              Active Rules
            </Typography>
            <Typography variant="h3">{hardRules.filter((r) => r.isActive).length}</Typography>
          </Paper>
        </Box>

        {/* Rules Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Conditions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hardRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{rule.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {rule.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={rule.category} color={getCategoryColor(rule.category)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={`P${rule.priority}`} color={rule.priority === 1 ? 'error' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{rule.conditions.length} condition(s)</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rule.isActive ? 'Active' : 'Inactive'}
                      color={rule.isActive ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleActive(rule.id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(rule)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(rule.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogMode !== null} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{dialogMode === 'create' ? 'Create New Hard Rule' : 'Edit Hard Rule'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <RuleBuilder
                rule={selectedRule || undefined}
                ruleType="hard"
                onSubmit={handleSubmit}
                onCancel={handleCloseDialog}
              />
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  );
};

export default HardRules;
